/**
 * TanStack Query hooks — rapports de comparaison (chunk 9.AI.1).
 *
 *   GET  /v1/comparison-reports?project_id=…    liste (métadonnées seules)
 *   POST /v1/comparison-reports                 génère ou rafraîchit le brouillon
 *   GET  /v1/comparison-reports/:id             relit en RECONSTRUISANT le contenu
 *   POST /v1/comparison-reports/:id/confirm     confirme, une fois
 *
 * ## Ce que l'interface ne fait pas
 *
 * Elle ne recalcule aucune comparaison. Le contenu arrive projeté par l'API —
 * `construireComparaison()` → `projectReport()` — et le composant se contente de
 * le rendre. Reconstruire une règle ici, même une seule, permettrait à l'écran de
 * dire autre chose que le rapport sur les mêmes données.
 *
 * ## Pourquoi une relecture après génération
 *
 * `POST` rend déjà le contenu, mais le rechargement d'une page confirmée doit
 * repasser par `GET /:id` : c'est ce qui prouve, à l'usage, que le rapport se
 * reconstruit depuis les sources courantes et n'est pas relu depuis une copie —
 * il n'y en a pas.
 *
 * Codes d'erreur attendus côté UI (bilingues) :
 *   COMPARISON_REPORTS_NOT_FOUND            (404)
 *   COMPARISON_REPORTS_PROJECT_NOT_FOUND    (404 anti-leak)
 *   COMPARISON_REPORTS_NOTHING_TO_REPORT    (422)
 *   COMPARISON_REPORTS_ALREADY_CONFIRMED    (409)
 *   COMPARISON_REPORTS_DRAFT_CONFLICT       (409)
 *   COMPARISON_REPORTS_FORBIDDEN_USER_ONLY  (403, wording neutre)
 */

import type {
  Comparison,
  ComparisonReportAudience,
  ComparisonReportLanguage,
  ComparisonReportRow,
} from "@kollectivia/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";

/** La charge rendue par les trois routes qui produisent du contenu. */
export type ComparisonReportPayload = {
  report: ComparisonReportRow;
  projection: Comparison.ReportProjection;
  /**
   * Les références du rapport ne décrivent plus tout à fait ce qu'il affiche.
   *
   * Ce n'est pas une anomalie : la table ne copie aucune valeur, donc une
   * extraction rejouée ou une correction humaine postérieure change ce qu'une
   * reconstruction montre. L'interface doit le DIRE plutôt que de laisser croire
   * qu'un rapport confirmé est figé.
   */
  sources_changed: boolean;
};

const CLE = ["comparison-reports"] as const;

export function useComparisonReports(projectId: string | undefined) {
  return useQuery({
    queryKey: [...CLE, "by-project", projectId],
    queryFn: () => api.get<ComparisonReportRow[]>(`/v1/comparison-reports?project_id=${projectId}`),
    enabled: !!projectId,
    placeholderData: (prev) => prev,
  });
}

export function useComparisonReport(reportId: string | undefined) {
  return useQuery({
    queryKey: [...CLE, "one", reportId],
    queryFn: () => api.get<ComparisonReportPayload>(`/v1/comparison-reports/${reportId}`),
    enabled: !!reportId,
  });
}

export type GenerateReportVars = {
  project_id: string;
  audience: ComparisonReportAudience;
  language: ComparisonReportLanguage;
};

export function useGenerateComparisonReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: GenerateReportVars) =>
      api.post<ComparisonReportPayload>("/v1/comparison-reports", vars),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CLE });
    },
  });
}

export function useConfirmComparisonReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reportId: string) =>
      api.post<ComparisonReportPayload>(`/v1/comparison-reports/${reportId}/confirm`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CLE });
    },
  });
}
